/**
 * AI leasing agent settings for one listing (BR-ENABLE-CASCADE, BR-AGENT-POLICY-SCOPE).
 *
 * Shows the RESOLVED enablement and review requirement as plain Off/On and Yes/No choices, each
 * captioned with where the current value comes from. There is no "inherit" control: saving writes
 * both values explicitly at listing scope. Server entitlement (BR-101) is unchanged — this only
 * makes the policy visible and editable before publish.
 *
 *   default   the live edit sheet: its own "Confirm agent settings" button.
 *   embedded  hosted in the listing guide's AI leasing agent step: no button of its own; the step's
 *             "Save and continue" calls `handleRef.save()`, which saves both values explicitly even
 *             when unchanged (so the agent row counts as confirmed, never merely defaulted).
 *
 * Reads and writes through useListingAgentPolicy's single react-query key, so the header pill and
 * the guide rail update in place after a save.
 */

import { useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';
import type { AgentPolicyResponse } from '@resida/api-types/agent';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { SegmentedRadioGroup } from '../../../components/email/SegmentedRadioGroup';
import type { GuideStepHandle } from '../listing-guide/guide-steps';
import { describePolicySource, useConfirmListingAgentPolicy, useListingAgentPolicy } from './useListingAgentPolicy';

type Enablement = AgentPolicyResponse['resolvedEnablement'];
type Review = AgentPolicyResponse['resolvedReview'];

const ENABLEMENT_LABELS: Record<Enablement, string> = { disabled: 'Off', enabled: 'On' };
const REVIEW_LABELS: Record<Review, string> = { required: 'Yes', not_required: 'No' };

/** " · set for this listing" when the value has a named source; nothing for the platform fallback. */
const sourceNote = (source: Parameters<typeof describePolicySource>[0]): string => {
  const described = describePolicySource(source);
  return described ? ` · ${described}` : '';
};

interface AgentPolicyEditorProps {
  listingId: string;
  canManage: boolean;
  /** Called after both values were saved (the setup row collapses / the sheet closes). */
  onConfirmed?: () => void;
  /** Draft only: the "Saved when you continue. The agent only starts once you publish." helper. */
  isDraft?: boolean;
  /** Hosted in a guide step: hide the confirm button; the step saves through `handleRef`. */
  embedded?: boolean;
  handleRef?: Ref<GuideStepHandle>;
  /** Fires when the unsaved-edits flag flips. */
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

export function AgentPolicyEditor({
  listingId,
  canManage,
  onConfirmed,
  isDraft = false,
  embedded = false,
  handleRef,
  onDirtyChange,
  onSavingChange,
}: AgentPolicyEditorProps) {
  const policyQuery = useListingAgentPolicy(listingId, canManage);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const formRef = useRef<GuideStepHandle>(null);

  // The handle outlives the form (which only exists once the policy loaded): a viewer has nothing
  // to save (true); an unloaded or failed policy cannot be saved (false, the load error shows).
  useImperativeHandle(
    handleRef,
    () => ({
      save: async () => (canManage ? (formRef.current ? formRef.current.save() : false) : true),
      isDirty: () => formRef.current?.isDirty() ?? false,
    }),
    [canManage]
  );

  return (
    <div id="agent-policy-section" data-testid="listing-agent-policy-section" className="scroll-mt-4">
      {/* Embedded in the guide, the step itself carries this intro under its heading. */}
      {!embedded && (
        <p className="text-sm text-muted-foreground mb-4">
          If you turn it on, the agent answers prospect questions about this listing once it is live.
        </p>
      )}

      {!canManage ? (
        <p className="text-sm text-muted-foreground" data-testid="listing-agent-policy-readonly">
          Only people who can manage this listing can view or change its AI agent settings.
        </p>
      ) : policyQuery.isPending ? (
        <div className="space-y-3" data-testid="listing-agent-policy-loading" aria-busy="true">
          <Skeleton className="h-11 w-full sm:w-64" />
          <Skeleton className="h-11 w-full sm:w-64" />
          <Skeleton className="h-11 w-44" />
        </div>
      ) : policyQuery.isError ? (
        <div
          role="alert"
          data-testid="listing-agent-policy-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
        >
          <p>Could not load the AI agent settings for this listing.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 min-h-[44px]"
            data-testid="listing-agent-policy-retry"
            onClick={() => void policyQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        // Re-seed the local choices whenever the server's resolved state changes (after a save,
        // or an account-default change elsewhere) — not on every background refetch.
        <AgentPolicyForm
          key={`${policyQuery.data.resolvedEnablement}|${policyQuery.data.resolvedReview}|${policyQuery.data.enablementSource}|${policyQuery.data.reviewSource}`}
          listingId={listingId}
          policy={policyQuery.data}
          isDraft={isDraft}
          embedded={embedded}
          formRef={formRef}
          onDirtyChange={onDirtyChange}
          onSavingChange={onSavingChange}
          successMsg={successMsg}
          onSaved={() => {
            setSuccessMsg('Agent settings saved.');
            setTimeout(() => setSuccessMsg(null), 3000);
            onConfirmed?.();
          }}
        />
      )}
    </div>
  );
}

interface AgentPolicyFormProps {
  listingId: string;
  policy: AgentPolicyResponse;
  isDraft: boolean;
  embedded: boolean;
  formRef: Ref<GuideStepHandle>;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  successMsg: string | null;
  onSaved: () => void;
}

function AgentPolicyForm({
  listingId,
  policy,
  isDraft,
  embedded,
  formRef,
  onDirtyChange,
  onSavingChange,
  successMsg,
  onSaved,
}: AgentPolicyFormProps) {
  const [enablement, setEnablement] = useState<Enablement>(policy.resolvedEnablement);
  const [review, setReview] = useState<Review>(policy.resolvedReview);
  const confirm = useConfirmListingAgentPolicy(listingId);
  const dirty = enablement !== policy.resolvedEnablement || review !== policy.resolvedReview;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(confirm.isPending);
  }, [confirm.isPending, onSavingChange]);

  // mutateAsync, not mutate(..., { onSuccess }): the hook's own onSuccess writes the new policy into
  // the query cache, which re-keys (remounts) this form, and react-query drops per-call callbacks of
  // an unmounted observer — so the edit sheet / step intermittently failed to advance. The error
  // stays on `confirm.error`, which the form renders. Both values are always sent: an unchanged
  // choice still becomes an explicit listing-scope value.
  const save = (): Promise<boolean> =>
    confirm.mutateAsync({ enablement, review }).then(
      () => {
        onSaved();
        return true;
      },
      () => false
    );

  useImperativeHandle(formRef, () => ({ save, isDirty: () => dirtyRef.current }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void save();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="listing-agent-policy-form">
      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground" id="listing-agent-enablement-label">
              Agent replies to prospects
            </p>
            <p className="text-xs text-muted-foreground" data-testid="listing-agent-enablement-caption">
              Currently {ENABLEMENT_LABELS[policy.resolvedEnablement]}
              {sourceNote(policy.enablementSource)}
            </p>
          </div>
          <SegmentedRadioGroup<Enablement>
            ariaLabel="Agent replies to prospects"
            testId="listing-agent-enablement-group"
            className="self-start shrink-0"
            value={enablement}
            onChange={setEnablement}
            options={[
              { value: 'disabled', label: 'Off', testId: 'listing-agent-enablement-off' },
              { value: 'enabled', label: 'On', testId: 'listing-agent-enablement-on' },
            ]}
          />
        </div>
        <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Review replies before they send</p>
            <p className="text-xs text-muted-foreground" data-testid="listing-agent-review-caption">
              Currently {REVIEW_LABELS[policy.resolvedReview]}
              {sourceNote(policy.reviewSource)}. Applies when the agent is on.
            </p>
          </div>
          <SegmentedRadioGroup<Review>
            ariaLabel="Review replies before they send"
            testId="listing-agent-review-group"
            className="self-start shrink-0"
            value={review}
            onChange={setReview}
            options={[
              { value: 'required', label: 'Yes', testId: 'listing-agent-review-required' },
              { value: 'not_required', label: 'No', testId: 'listing-agent-review-not-required' },
            ]}
          />
        </div>
      </div>

      {confirm.isError && (
        <div
          role="alert"
          data-testid="listing-agent-policy-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
        >
          {confirm.error instanceof Error ? confirm.error.message : 'Failed to save the agent settings'}
        </div>
      )}

      {successMsg && (
        <div
          role="status"
          aria-live="polite"
          data-testid="listing-agent-policy-success"
          className="p-3 bg-success/10 border border-success/25 rounded-md text-success text-sm"
        >
          {successMsg}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        {!embedded && (
          <Button
            type="submit"
            className="min-h-[44px]"
            disabled={confirm.isPending}
            data-testid="listing-agent-policy-save"
          >
            {confirm.isPending ? 'Saving…' : 'Confirm agent settings'}
          </Button>
        )}
        {isDraft && (
          <span className="text-xs text-muted-foreground" data-testid="listing-agent-policy-draft-note">
            Saved when you continue. The agent only starts once you publish.
          </span>
        )}
      </div>
    </form>
  );
}
